import React, { useEffect } from 'react';
import { styles } from './HelpAndSupportPage.css';
import { HelpAndSupportLanding } from '@jbhi-fi/components/src/HelpAndSupport/HelpAndSupportLanding/HelpAndSupportLanding';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useContentfulHelpAndSupport } from '@jbhi-fi/components/src/hooks/useContentfulHelpAndSupport/useContentfulHelpAndSupport';
import { SupportLandingPageTransformer } from '@jbhi-fi/components/src/Contentful/transformers/SupportLandingPageTransformer';
import { SupportCategory } from '@jbhi-fi/components/src/HelpAndSupport/SupportCategory/SupportCategory';
import { SupportCategoryTransformer } from '@jbhi-fi/components/src/Contentful/transformers/SupportCategoryTransformer';
import { SupportArticle } from '@jbhi-fi/components/src/supportArticle/SupportArticle';
import { SupportArticleTransformer } from '@jbhi-fi/components/src/supportArticle/SupportArticleTransformer';
import { BreadcrumbItemProps, SimpleBreadcrumbs } from '@jbhi-fi/components/src/SimpleBreadcrumbs/SimpleBreadcrumbs';
import { getBreadCrumbs } from './utils';
import LoadingIcon from '@jbhi-fi/lanyard-ui/components/Icons/Loading';
import { SupportSearchResults } from '@jbhi-fi/components/src/HelpAndSupport/SearchResults/SupportSearchResults';
import { HelpAndSupportSearch } from '@jbhi-fi/components/src/HelpAndSupport/HelpAndSupportSearch/HelpAndSupportSearch';

const BASE_TITLE = 'Help & Support - JB Hi-Fi';

const Layout = ({
  breadcrumbs,
  isSearchEnabled = true,
  children,
}: {
  breadcrumbs: Array<BreadcrumbItemProps>;
  isSearchEnabled?: boolean;
  landingPage?: boolean;
  children: React.ReactNode;
}) => {
  return (
    <div>
      <div className={styles.topSection}>
        <div className={styles.breadcrumbContainer}>
          <SimpleBreadcrumbs breadcrumbs={breadcrumbs} />
        </div>
        {isSearchEnabled ? (
          <div className={styles.searchContainer}>
            <HelpAndSupportSearch />
          </div>
        ) : null}
      </div>
      <div className={styles.container}>
        <div className={styles.pageWidth}>{children}</div>
      </div>
    </div>
  );
};

export const HelpAndSupportPage = () => {
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const searchValue = params.get('query') ?? '';
  const handle = pathname.replace(/^\/|\/$/g, '').toLowerCase();
  const isPreview = window.Jbhifi.isContentPreview;
  const { landingPage, categoryPage, articlePage, isLoading } = useContentfulHelpAndSupport({ handle, isPreview });
  const breadcrumbs = getBreadCrumbs(articlePage, categoryPage, handle);
  const newHandle = categoryPage?.urlSlug || articlePage?.fields?.urlSlug;
  const pageTitle = articlePage?.fields?.heading || categoryPage?.title;

  useEffect(() => {
    if (!handle) {
      document.title = BASE_TITLE;
      return;
    }
    if (handle === 'search') {
      document.title = `Search - ${BASE_TITLE}`;
      return;
    }
    if (pageTitle) {
      document.title = `${pageTitle} - ${BASE_TITLE}`;
    }
  }, [pageTitle, handle]);

  useEffect(() => {
    const url = new URL(window.location.href);

    if (newHandle) {
      url.pathname = `/pages/help-and-support/${newHandle}`;
    }

    if (isPreview) {
      url.searchParams.set('preview', 'true');
    } else {
      url.searchParams.delete('preview');
    }

    window.history.replaceState({}, '', url.toString());
  }, [newHandle, isPreview]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <LoadingIcon height={50} width={50} />
      </div>
    );
  }
  const isSearchEnabled = handle !== '' && handle !== 'search';

  return (
    <Switch>
      <Route exact path="/">
        {landingPage ? (
          <HelpAndSupportLanding {...SupportLandingPageTransformer.toHelpAndSupportLandingPageProps(landingPage)} />
        ) : null}
      </Route>
      <Route path="/search">
        <Layout breadcrumbs={breadcrumbs} isSearchEnabled={false}>
          <SupportSearchResults searchValue={searchValue?.trim()} />
        </Layout>
      </Route>

      <Route>
        <Layout breadcrumbs={breadcrumbs} isSearchEnabled={isSearchEnabled} landingPage={handle === ''}>
          {categoryPage ? (
            <SupportCategory {...SupportCategoryTransformer.toSupportCategoryProps(categoryPage)} handle={handle} />
          ) : null}
          {articlePage ? (
            <div className={styles.articleWrapper}>
              <SupportArticle {...SupportArticleTransformer.toSupportArticleProps(articlePage)} />
            </div>
          ) : null}
          {!categoryPage && !articlePage ? <Redirect to="/" /> : null}
        </Layout>
      </Route>
    </Switch>
  );
};
